import React, {useContext, useEffect, useMemo} from "react";
import {useState, useRef} from "react";
import {useNavigate, useParams} from "react-router-dom";
import useMain from "../../../hooks/useMain";
import {StoreContext} from "../../../store/StoreContext";
import {swalAlert, toast} from "../../../alerts/sweetalert";
import {
  LOCAL_NEW_STATE,
  LOCAL_UPDATE_STATE,
  SEND_STATE,
} from "../../../constants/states";
import DataTable from "react-data-table-component";
import {LIMIT_SAVEDFORMS} from "../../../constants";
import {COLOR_DANGER} from "../../../constants/colors";
import Paginator from "../../../components/paginator/Paginator";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRotateRight, faEye, faPenToSquare, faTrash, faSync} from "@fortawesome/free-solid-svg-icons";
import DialogPreviewFinding from "../../../components/dialog-preview-finding/DialogPreviewFinding";
import DialogPreviewCommitment from "../../../components/dialog-preview-commitment/DialogPreviewCommitment";
import {getLocal, putLocal} from "../../../helpers/local-storage";
import {getCenters} from "../../../mongo-db/centers";

const paginationComponentOptions = {
  rowsPerPageText: "Filas por página",
  rangeSeparatorText: "de",
  selectAllRowsItem: false,
  selectAllRowsItemText: "Todos",
};

const STORAGE_KEY = "myInspections_filters";

const MyInspections = () => {
  const {setAppTitle, session, online, myInspections, myInspectionsPending} =
    useContext(StoreContext);
  const [cachedAreas, setCachedAreas] = useState(() => getLocal("sso_areas_cache", []));
  // Mapa solo para el dropdown de filtro de centros
  const {rol} = useParams();
  const navigate = useNavigate();
  const {fetchSavedForms, syncNewForm, deleteForm, syncUpdatedForm} = useMain();

  // Restaurar estado guardado si existe (la limpieza se hace en el unmount)
  const saved = useRef((() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })()).current;

  const [inspections, setInspections] = useState([]);
  const [search, setSearch] = useState(saved?.search || "");
  const [debouncedSearch, setDebouncedSearch] = useState(saved?.search || "");
  const [fromDate, setFromDate] = useState(saved?.fromDate || "");
  const [toDate, setToDate] = useState(saved?.toDate || "");
  const [templateId, setTemplateId] = useState(null);
  const [openingFinding, setOpeningFinding] = useState(false);
  const [formId, setFormId] = useState(null);
  const [commitTemplateId, setCommitTemplateId] = useState(null);
  const [openingCommitment, setOpeningCommitment] = useState(false);
  const [commitFormId, setCommitFormId] = useState(null);
  const [currentPage, setCurrentPage] = useState(saved?.currentPage || 1);
  const [rowsPerPage, setRowsPerPage] = useState(saved?.rowsPerPage || LIMIT_SAVEDFORMS);
  const [totalRows, setTotalRows] = useState(0);
  const [ready, setReady] = useState(false);
  const [sortBy, setSortBy] = useState(saved?.sortBy || "FechaFormulario");
  const [sortDir, setSortDir] = useState(saved?.sortDir || "desc");
  const [contentFilter, setContentFilter] = useState(saved?.contentFilter || "");
  const [tableKey, setTableKey] = useState(0);
  const [centroFilter, setCentroFilter] = useState(saved?.centroFilter || "");

  const showCentro = true;

  // Flag para indicar que la navegacion fue hacia un detalle (no limpiar sessionStorage)
  const navigatingToDetailRef = useRef(false);

  const instalacionOptions = useMemo(() => {
    const set = new Set();
    cachedAreas.forEach(area => {
      if (!area?.centers) return;
      area.centers.forEach(c => { if (c?.title) set.add(c.title); });
    });
    return [...set].sort((a, b) => a.localeCompare(b, "es"));
  }, [cachedAreas]);

  // Persistir filtros en sessionStorage en cada cambio de estado
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      search, fromDate, toDate, currentPage, rowsPerPage, sortBy, sortDir, contentFilter, centroFilter,
    }));
  }, [search, fromDate, toDate, currentPage, rowsPerPage, sortBy, sortDir, contentFilter, centroFilter]);

  const reloadSavedForms = async (
    username = session?.username,
    page = currentPage,
    limit = rowsPerPage
  ) => {
    if (!username) {
      console.log('[MyInspections.reloadSavedForms] Sin username, retornando');
      return;
    }

    const pagination = await fetchSavedForms(username, {
      page,
      limit,
      sortBy,
      sortDir,
      search: debouncedSearch,
      contentFilter,
      centerName: centroFilter,
      fromDate,
      toDate,
    });
    const pageAux = Number(pagination?.page) || page;
    const limitAux = Number(pagination?.limit) || limit;
    const totalAux = Number(pagination?.total) || 0;

    setCurrentPage(pageAux);
    setRowsPerPage(limitAux);
    setTotalRows(totalAux);
    if (!ready) setReady(true);
  };

  const renderState = stateAux => {
    let component = <></>;

    if (stateAux === LOCAL_NEW_STATE)
      component = (
        <span className="badge bg-warning text-dark">
          Pendiente para guardado
        </span>
      );
    else if (stateAux === LOCAL_UPDATE_STATE)
      component = (
        <span className="badge bg-warning text-dark">
          Pendiente para actualizar
        </span>
      );
    else component = <span className="badge bg-success">Enviado</span>;

    return component;
  };

  const goToForms = (IdForm, formType) => {
    navigatingToDetailRef.current = true;
    navigate(
      `/cermaq/roles/${rol ? rol + "/" : ""}forms/${formType}/${IdForm}`
    );
  };

  const goToPreviewForms = (IdForm, formType) => {
    navigatingToDetailRef.current = true;
    navigate(
      `/cermaq/roles/${rol ? rol + "/" : ""}forms/preview/${formType}/${IdForm}`
    );
  };

  const handleSyncNewForm = async data => {
    await syncNewForm(data);
    await reloadSavedForms();
    toast({icon: "success", title: "Inspección sincronizada."});
  };

  const handleSyncUpdatedForm = async data => {
    await syncUpdatedForm(data);
    await reloadSavedForms();
    toast({icon: "success", title: "Inspección sincronizada."});
  };

  const remove = async fId => {
    const {isConfirmed} = await swalAlert(
      "¿Deseas eliminar esta inspección?",
      null,
      "Eliminar",
      "Cancelar",
      COLOR_DANGER
    );
    if (!isConfirmed) return;

    await deleteForm(fId);
    await reloadSavedForms();
    toast({icon: "success", title: "Inspección eliminada."});
  };

  const previewFinding = (fId, formType) => {
    if (openingFinding) return;
    setTemplateId(formType);
    setFormId(fId);
  };

  const closeFinding = () => {
    if (openingFinding) return;
    setTemplateId(null);
    setFormId(null);
  };

  const previewCommitment = (fId, formType) => {
    if (openingCommitment) return;
    setCommitTemplateId(formType);
    setCommitFormId(fId);
  };

  const closeCommitment = () => {
    if (openingCommitment) return;
    setCommitTemplateId(null);
    setCommitFormId(null);
  };

  useEffect(() => {
    if (navigator.onLine) {
      getCenters().then(data => {
        if (data?.length) {
          putLocal("sso_areas_cache", data);
          setCachedAreas(data);
        }
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    setAppTitle("Mis Inspecciones");
    // Limpiar sessionStorage al desmontar SOLO si no estamos navegando a un detalle.
    // Asi, al volver de Ver/Editar se restauran los filtros,
    // pero al ir a otra seccion se empieza limpio.
    return () => {
      if (!navigatingToDetailRef.current) {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    };
  }, []);

  useEffect(() => {
    const username = session?.username;
    if (username) reloadSavedForms(username, currentPage, rowsPerPage);
  }, [session?.username, currentPage, rowsPerPage, debouncedSearch, sortBy, sortDir, contentFilter, centroFilter, fromDate, toDate]);

  useEffect(() => {
    let _inspections = myInspections;
    if (rol)
      _inspections = _inspections.filter(i => {
        const _rol = "- " + rol;
        return i.Titulo.includes(_rol);
      });
    setInspections(_inspections);
  }, [myInspections, rol]);

  const searchInitRef = useRef(true);
  useEffect(() => {
    if (searchInitRef.current) {
      searchInitRef.current = false;
      return; // No debounce en mount/restore
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
      setTableKey(k => k + 1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const data = useMemo(() => {
    return inspections.map(inspect => ({
      ...inspect,
      Titulo: inspect.Titulo
        .replace("Inspector", "SSO")
        .replace("Jefatura", "Supervisor"),
      __instalacion: inspect.centerName || "",
    }));
  }, [inspections]);

  const columns = [
    {
      id: "titulo",
      name: "Nombre",
      selector: row => row.Titulo,
      width: showCentro ? "30%" : "40%",
      sortable: true,
      sortField: "Titulo",
    },
    ...(showCentro
      ? [
          {
            id: "instalacion",
            name: "Centro",
            selector: row => row.__instalacion,
            width: "150px",
            sortable: true,
            sortField: "__instalacion",
          },
        ]
      : []),
    {
      id: "fecha",
      name: "Fecha Ingreso",
      selector: row => row.FechaFormulario,
      width: "200px",
      sortable: true,
      sortField: "FechaFormulario",
    },
    {
      id: "estado",
      name: "Estado",
      cell: row => renderState(row?.state),
      width: "180px",
      sortable: true,
      sortField: "state",
    },
    {
      name: "Acciones",
      width: "auto",
      cell: row => (
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn btn-sm mx-1 bg-primary text-white fs-6"
            onClick={() => goToPreviewForms(row.IdForm, row.Tipo)}
            title="Ver"
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
          <button
            type="button"
            className="btn btn-sm mx-1 bg-primary text-white fs-6"
            onClick={() => goToForms(row.IdForm, row.Tipo)}
            title="Editar"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
          {row.hallazgos?.length > 0 && (
            <button
              type="button"
              className="btn btn-sm text-nowrap mx-1 bg-primary text-white fs-6"
              onClick={() => previewFinding(row.IdForm, row.Tipo)}
            >
              Hallazgos
            </button>
          )}
          {row.compromisos?.length > 0 && (
            <button
              type="button"
              className="btn btn-sm text-nowrap mx-1 bg-primary text-white fs-6"
              onClick={() => previewCommitment(row.IdForm, row.Tipo)}
            >
              Compromisos
            </button>
          )}
          {row.state !== SEND_STATE && (
            <button
              type="button"
              className="btn btn-sm mx-1 bg-danger text-white fs-6"
              onClick={() => remove(row.IdForm)}
              disabled={!online}
              title="Eliminar"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
          {row?.state === LOCAL_NEW_STATE && online && (
            <button
              type="button"
              className="btn btn-sm mx-1 bg-warning text-white fs-6"
              onClick={() => handleSyncNewForm(row)}
              title="Sync Guardado"
            >
              <FontAwesomeIcon icon={faSync} />
            </button>
          )}
          {row?.state === LOCAL_UPDATE_STATE && online && (
            <button
              type="button"
              className="btn btn-sm mx-1 bg-warning text-white fs-6"
              onClick={() => handleSyncUpdatedForm(row)}
              title="Sync Actualización"
            >
              <FontAwesomeIcon icon={faSync} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <DialogPreviewFinding
        close={closeFinding}
        templateId={templateId}
        formId={formId}
        opening={openingFinding}
        setOpening={setOpeningFinding}
      />
      <DialogPreviewCommitment
        templateId={commitTemplateId}
        formId={commitFormId}
        close={closeCommitment}
        opening={openingCommitment}
        setOpening={setOpeningCommitment}
      />

      <div className="row">
        <div className="col-12 col-md-2 d-flex mb-2">
          <button
            type="button"
            className="btn bg-success text-white fw-semibold text-nowrap me-3 mt-auto"
            onClick={() => reloadSavedForms(session?.username, 1, rowsPerPage)}
            disabled={!session || myInspectionsPending}
          >
            <FontAwesomeIcon icon={faArrowRotateRight} className="me-2" />
            Refrescar
          </button>
        </div>

        <div className="col-12 col-md-4">
          <div className="row">
            <div className="col-6 mb-2">
              <label htmlFor="date-from-filter" className="form-label">
                Desde
              </label>
              <input
                id="date-from-filter"
                type="date"
                className="form-control"
                max={toDate}
                value={fromDate}
                onChange={({target}) => { setFromDate(target.value); setCurrentPage(1); setTableKey(k => k + 1); }}
                placeholder="Selecciona fecha"
                aria-label="Selecciona fecha"
                aria-describedby="addon-wrapping"
              />
            </div>
            <div className="col-6 mb-2">
              <label htmlFor="date-to-filter" className="form-label">
                Hasta
              </label>
              <input
                id="date-to-filter"
                type="date"
                className="form-control"
                min={fromDate}
                value={toDate}
                onChange={({target}) => { setToDate(target.value); setCurrentPage(1); setTableKey(k => k + 1); }}
                placeholder="Selecciona fecha"
                aria-label="Selecciona fecha"
                aria-describedby="addon-wrapping"
              />
            </div>
          </div>
        </div>

        {showCentro && (
          <div className="col-6 col-md-2 mb-2">
            <label htmlFor="centro-filter" className="form-label">
              Centro
            </label>
            <select
              id="centro-filter"
              className="form-select"
              value={centroFilter}
              onChange={({target}) => {
                setCentroFilter(target.value);
                setCurrentPage(1);
                setTableKey(k => k + 1);
              }}
            >
              <option value="">Todos</option>
              {instalacionOptions.map(name => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="col-6 col-md-2 mb-2">
          <label htmlFor="content-filter" className="form-label">
            Contenido
          </label>
          <select
            id="content-filter"
            className="form-select"
            value={contentFilter}
            onChange={({target}) => {
              setContentFilter(target.value);
              setCurrentPage(1);
              setTableKey(k => k + 1);
            }}
          >
            <option value="">Todos</option>
            <option value="hallazgos">Con Hallazgos</option>
            <option value="compromisos">Con Compromisos</option>
          </select>
        </div>

        <div className="col-12 col-md-2 mb-2">
          <label htmlFor="search" className="form-label">
            Buscar
          </label>
          <input
            id="search"
            type="search"
            className="form-control"
            placeholder="Buscar por nombre..."
            value={search}
            onChange={({target}) => setSearch(target.value)}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="table-responsive">
            {session?.username && (
              <DataTable
                key={tableKey}
                className="table"
                columns={columns}
                data={data}
                progressPending={!ready || myInspectionsPending}
                pagination
                paginationServer
                sortServer
                paginationTotalRows={totalRows}
                paginationPerPage={rowsPerPage}
                paginationDefaultPage={currentPage}
                onChangePage={page => setCurrentPage(page)}
                onChangeRowsPerPage={perPage => { setRowsPerPage(perPage); setCurrentPage(1); }}
                onSort={(column, direction) => {
                  const newBy = column.sortField || "FechaFormulario";
                  const newDir = direction === "desc" ? "desc" : "asc";
                  if (newBy === sortBy && newDir === sortDir) return;
                  setSortBy(newBy);
                  setSortDir(newDir);
                  setCurrentPage(1);
                }}
                paginationRowsPerPageOptions={[10]}
                noDataComponent={
                  <div className="mt-5">Sin datos para mostrar</div>
                }
                paginationComponentOptions={paginationComponentOptions}
                paginationComponent={Paginator}
                defaultSortFieldId="fecha"
                defaultSortAsc={false}
                progressComponent={
                  <div className="text-center text-primary-cermaq mt-5">
                    <div className="spinner-border" role="status" />
                  </div>
                }
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MyInspections;
